import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  ChartBarIcon,
  FlagIcon,
  SparklesIcon,
  ScaleIcon,
  FireIcon,
  CakeIcon,
  HeartIcon,
  BoltIcon,
  ShareIcon
} from '@heroicons/react/24/outline'

// Import components
import HealthCycleTab from '../components/progress/HealthCycleTab'
import UntilNextTestSection from '../components/progress/UntilNextTestSection'
import GoalCalculator from '../components/profile/GoalCalculator'
import NutritionGoalsCard from '../components/profile/NutritionGoalsCard'
import BodyCompositionChart from '../components/progress/BodyCompositionChart'
import WeeklyComparisonGrid from '../components/progress/WeeklyComparisonGrid'
import WeeklyExerciseProgress from '../components/progress/WeeklyExerciseProgress'
import DailyNutritionTab from '../components/progress/DailyNutritionTab'
import WellnessCheckIn from '../components/progress/WellnessCheckIn'
import ActivityTab from '../components/progress/ActivityTab'
import SleepRecoveryTab from '../components/sleep/SleepRecoveryTab'
import TrainingAnalysisTab from '../components/training-analysis/TrainingAnalysisTab'
import SystemViewTab from '../components/system-view/SystemViewTab'
// VH-61: Overview summary cards
import ActivitySummaryCard from '../components/progress/ActivitySummaryCard'
import SleepSummaryCard from '../components/progress/SleepSummaryCard'
import ExerciseSummaryCard from '../components/progress/ExerciseSummaryCard'
import BodyTrackingSummaryCard from '../components/progress/BodyTrackingSummaryCard'
const TABS = [
  { id: 'overview', icon: ChartBarIcon, labelKey: 'myProgress.tabs.overview' },
  { id: 'goals', icon: FlagIcon, labelKey: 'myProgress.tabs.goals' },
  { id: 'nutrition', icon: CakeIcon, labelKey: 'myProgress.tabs.nutrition' },
  { id: 'wellness', icon: HeartIcon, labelKey: 'myProgress.tabs.wellness' },
  { id: 'activity', icon: BoltIcon, labelKey: 'myProgress.tabs.activity' },
  { id: 'exercises', icon: FireIcon, labelKey: 'myProgress.tabs.exercises' },
  { id: 'bodyTracking', icon: ScaleIcon, labelKey: 'myProgress.tabs.bodyTracking' },
  { id: 'healthCycle', icon: SparklesIcon, labelKey: 'myProgress.tabs.healthCycle' },
  { id: 'systemView', icon: ShareIcon, labelKey: 'myProgress.tabs.systemView' }
]

export default function MyProgress() {
  const { t } = useTranslation()
  const [activeTab, setActiveTab] = useState('overview')

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <div className="space-y-6">
            {/* Compact Goals Summary */}
            <div className="card">
              <div className="card-body">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center">
                    <FlagIcon className="h-5 w-5 text-indigo-600 mr-2" />
                    <h4 className="font-medium text-gray-900">
                      {t('myProgress.currentGoals', 'Current Goals')}
                    </h4>
                  </div>
                  <button
                    onClick={() => setActiveTab('goals')}
                    className="text-sm text-indigo-600 hover:text-indigo-800"
                  >
                    {t('myProgress.viewDetails', 'View Details')} →
                  </button>
                </div>
                <NutritionGoalsCard compact />
              </div>
            </div>

            {/* VH-61: Summary Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <ActivitySummaryCard onNavigate={setActiveTab} />
              <SleepSummaryCard onNavigate={setActiveTab} />
              <ExerciseSummaryCard onNavigate={setActiveTab} />
              <BodyTrackingSummaryCard onNavigate={setActiveTab} />
            </div>

            {/* Weekly Nutrition vs Activity Comparison */}
            <WeeklyComparisonGrid />
          </div>
        )

      case 'nutrition':
        return <DailyNutritionTab />

      case 'wellness':
        return (
          <div className="space-y-8">
            <WellnessCheckIn />
            <SleepRecoveryTab />
          </div>
        )

      case 'activity':
        return <ActivityTab />

      case 'exercises':
        return (
          <div className="space-y-8">
            <WeeklyExerciseProgress />
            <TrainingAnalysisTab />
          </div>
        )

      case 'systemView':
        return <SystemViewTab />

      case 'goals':
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <NutritionGoalsCard />
              <GoalCalculator />
            </div>
            <UntilNextTestSection />
          </div>
        )

      case 'healthCycle':
        return <HealthCycleTab />

      case 'bodyTracking':
        return (
          <div className="space-y-6">
            <BodyCompositionChart />
          </div>
        )

      default:
        return null
    }
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">
          {t('myProgress.title', 'My Progress')}
        </h1>
        <p className="mt-2 text-gray-600">
          {t('myProgress.description', 'Track your health journey, set goals, and get AI-powered insights')}
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8 overflow-x-auto">
          {TABS.map((tab) => {
            const Icon = tab.icon
            const isActive = activeTab === tab.id
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  flex items-center py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap
                  ${isActive
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }
                `}
              >
                <Icon className={`h-5 w-5 mr-2 ${isActive ? 'text-indigo-500' : 'text-gray-400'}`} />
                {t(tab.labelKey, tab.id)}
              </button>
            )
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="max-w-6xl">
        {renderTabContent()}
      </div>
    </div>
  )
}
